import styled from '@emotion/styled';
import type { Thread } from '@hotjar/comments/domains/model';
import { CommentContent } from '@hotjar/comments/components/CommentContent';
import { Link, tokens } from '@hotjar/hotsauce';
import { truncate } from 'lodash-es';
import { useTranslation } from 'react-i18next';

export const HIGHLIGHT_COMMENTS_PREVIEW_HEIGHT = 65;

const Wrapper = styled.div`
  position: absolute;
  width: 100%;
  height: ${HIGHLIGHT_COMMENTS_PREVIEW_HEIGHT}px;
  left: 0px;
  top: 160px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  background-color: ${tokens.BRAND_COLORS.white};
  padding: ${tokens.spacer.x2};
`;

const CommentPreview = styled(CommentContent)<{ hasMoreComments: boolean }>`
  padding: 0px;
  grid-column-gap: 3px;
  align-items: center;
  margin-top: ${(props) => (props.hasMoreComments ? '-12px' : '0')};
  .avatar {
    transform: scale(calc(24 / 32));
  }

  .comment-content {
    width: 100%;
    max-height: 36px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    .comment-author {
      display: inline;
    }

    .comment-text {
      display: inline;
    }

    .comment-metadata {
      display: none;
    }
  }
`;

const MoreCommentsCount = styled(Link)`
  font-size: 12px;
  color: ${tokens.color.neutral_500};
  position: absolute;
  bottom: 9px;
  left: 50px;
`;

interface HighlightCommentPreviewProps {
  threads: Thread[];
  currentUserId: number | undefined;
}

export function HighlightCommentPreview({
  threads,
  currentUserId,
}: HighlightCommentPreviewProps): JSX.Element | null {
  const { t } = useTranslation();

  if (!threads.length || currentUserId === undefined) {
    return null;
  }

  const firstThread = {
    ...threads[0],
    author: {
      ...threads[0].author,
      name: truncate(threads[0].author.name, {
        length: 30,
      }),
    },
  };

  return (
    <Wrapper>
      <CommentPreview
        hasMoreComments={threads[0].replies.length > 0}
        currentUserId={currentUserId}
        comment={firstThread}
        renderCreator={() => null}
      />
      {threads[0].replies.length > 0 && (
        <MoreCommentsCount kind="inline">{`${threads[0].replies.length} ${t(
          'highlights:list.more',
        )}`}</MoreCommentsCount>
      )}
    </Wrapper>
  );
}
